import type { BarcodeFormat, EcLevel } from './barcode.ts';
import type { ImageSide } from './db.ts';

const TOKEN_KEY = 'klarnus.token';

export interface PublicUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  nationality: string;
  phone: string;
  address: string;
  verified: boolean;
  /** Created by the app on first open; no email, no password. */
  guest?: boolean;
  createdAt: number;
}

export interface RemoteCard {
  id: string;
  name: string;
  barcodeValue: string;
  barcodeFormat: BarcodeFormat;
  barcodeEcLevel?: EcLevel | null;
  expiresOn?: string | null;
  color: string | null;
  note: string | null;
  favorite: boolean;
  lastUsedAt: number | null;
  createdAt: number;
  updatedAt: number;
  deleted: boolean;
}

export interface RemotePlace {
  id: string;
  cardId: string;
  lat: number;
  lng: number;
  accuracy: number | null;
  label: string | null;
  createdAt: number;
  updatedAt: number;
  deleted: boolean;
}

export interface PushResult {
  serverTime: number;
  applied: string[];
  rejected: { id: string; reason: string }[];
  appliedPlaces?: string[];
  rejectedPlaces?: { id: string; reason: string }[];
}

export type RemotePrefs = { data: Record<string, unknown>; updatedAt: number };

export interface RemoteImageMeta {
  cardId: string;
  side: ImageSide;
  mime: string;
  updatedAt: number;
  deleted: boolean;
  size: number;
}

export interface PullResult {
  serverTime: number;
  cards: RemoteCard[];
  images: RemoteImageMeta[];
  /** Absent from servers older than the places feature. */
  places?: RemotePlace[];
  prefs: RemotePrefs | null;
}

/** Thrown for anything the server answered; a network failure throws a plain TypeError instead. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly issues?: { path: string; message: string }[],
  ) {
    super(code);
    this.name = 'ApiError';
  }
}

export const getToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

export const setToken = (token: string | null): void => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Private mode with storage blocked: the session lives for this tab only, which still works.
  }
};

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers = new Headers(init.headers);
  if (token) headers.set('authorization', `Bearer ${token}`);
  if (init.body && !headers.has('content-type')) headers.set('content-type', 'application/json');

  const res = await fetch(path, { ...init, headers });
  if (res.status === 204) return undefined as T;

  const isJson = res.headers.get('content-type')?.includes('application/json');
  const body = isJson ? await res.json() : null;

  if (!res.ok) {
    throw new ApiError(res.status, (body as { error?: string } | null)?.error ?? `http_${res.status}`,
      (body as { issues?: { path: string; message: string }[] } | null)?.issues);
  }
  return body as T;
}

export interface RegisterInput {
  firstName: string;
  lastName: string;
  email: string;
  nationality: string;
  password: string;
  phone?: string;
  address?: string;
}

export interface Session {
  token: string;
  expiresAt: number;
  user: PublicUser;
}

export const api = {
  register: (input: RegisterInput) =>
    request<{ status: string }>('/api/auth/register', { method: 'POST', body: JSON.stringify(input) }),
  guest: () => request<Session>('/api/auth/guest', { method: 'POST' }),
  verify: (token: string) =>
    request<Session>('/api/auth/verify', { method: 'POST', body: JSON.stringify({ token }) }),
  resend: (email: string) =>
    request<{ status: string }>('/api/auth/resend', { method: 'POST', body: JSON.stringify({ email }) }),
  login: (email: string, password: string) =>
    request<Session>('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  forgot: (email: string) =>
    request<{ status: string }>('/api/auth/forgot', { method: 'POST', body: JSON.stringify({ email }) }),
  reset: (token: string, password: string) =>
    request<{ status: string }>('/api/auth/reset', { method: 'POST', body: JSON.stringify({ token, password }) }),
  me: () => request<{ user: PublicUser }>('/api/auth/me'),
  updateProfile: (patch: Partial<Omit<PublicUser, 'id' | 'email' | 'verified' | 'createdAt'>>) =>
    request<{ user: PublicUser }>('/api/auth/me', { method: 'PATCH', body: JSON.stringify(patch) }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ status: string }>('/api/auth/password', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) }),
  logout: () => request<{ status: string }>('/api/auth/logout', { method: 'POST' }),
  deleteAccount: () => request<{ status: string }>('/api/auth/me', { method: 'DELETE' }),

  pull: (since: number) => request<PullResult>(`/api/sync?since=${since}`),
  push: (cards: RemoteCard[], places: RemotePlace[] = [], prefs?: RemotePrefs) =>
    request<PushResult>('/api/sync', {
      method: 'POST',
      body: JSON.stringify({ cards, ...(places.length ? { places } : {}), ...(prefs ? { prefs } : {}) }),
    }),

  async uploadImage(cardId: string, side: ImageSide, blob: Blob): Promise<void> {
    await request(`/api/cards/${cardId}/image/${side}`, {
      method: 'PUT', body: blob, headers: { 'content-type': blob.type || 'image/jpeg' },
    });
  },
  async downloadImage(cardId: string, side: ImageSide): Promise<Blob | null> {
    const res = await fetch(`/api/cards/${cardId}/image/${side}`, {
      headers: getToken() ? { authorization: `Bearer ${getToken()}` } : undefined,
    });
    if (res.status === 404) return null;
    if (!res.ok) throw new ApiError(res.status, `http_${res.status}`);
    return res.blob();
  },
  deleteImage: (cardId: string, side: ImageSide) =>
    request<{ status: string }>(`/api/cards/${cardId}/image/${side}`, { method: 'DELETE' }),

  pushKey: () => request<{ enabled: boolean; publicKey: string | null }>('/api/push/key'),
  pushSubscribe: (sub: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    request<{ status: string }>('/api/push/subscribe', { method: 'POST', body: JSON.stringify(sub) }),
  pushUnsubscribe: (endpoint: string) =>
    request<{ status: string }>('/api/push/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint }) }),
  pushTest: () => request<{ status: string; sent: number }>('/api/push/test', { method: 'POST' }),
};
